import Modal from '@uikit/Modal/Modal';
import React, { FC, useCallback, useEffect, useState } from 'react';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { useForm } from '@hooks/useDirtyForm';
import { ControlledInput } from '@uikit/FormItems/ControlledItems';
import {
  AuthModalProps,
  defaultValues,
  formDataType,
} from '@src/modals/AuthModal/constants';
import { validations } from '@utils/validations';
import { useLoginWithUserInfoInResMutation } from '@store/rtk/login';
import { toast } from 'react-toastify';
import { useSupervisorAccess } from '@hooks/useSupervisorAccess';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

const AuthModal: FC<AuthModalProps> = ({
  closeClick,
  toggleSupervisorOverride,
  dataTestId,
  success,
}) => {
  const { supervisorPermissions } = useSupervisorAccess();
  const [loggedInUserInfo, setLoggedInUserInfo] = useState<{
    firstName: string;
    lastName: string;
    id: number;
  } | null>(null);
  const [login, { isLoading, isSuccess }] = useLoginWithUserInfoInResMutation();

  const {
    handleSubmit,
    control,
    formState: { dirtyFields, isSubmitting, isValid },
  } = useForm<formDataType>({
    defaultValues,
  });

  useEffect(() => {
    if (isLoading) return;

    if (isSuccess && loggedInUserInfo !== null) {
      closeClick();
      toggleSupervisorOverride(true);
      success?.({ ...loggedInUserInfo });
    }

    return () => {
      setLoggedInUserInfo(null);
    };
  }, [isLoading, isSuccess, loggedInUserInfo]);

  const handleApproveSupervisor = useCallback(async (body: formDataType) => {
    try {
      if (
        supervisorPermissions.canDocument &&
        supervisorPermissions.canCreate
      ) {
        const response = await login({ body }).unwrap();

        setLoggedInUserInfo({
          firstName: response?.firstName,
          lastName: response?.lastName,
          id: response?.id,
        });
      } else {
        toast.error(toastMessage.COMMON.noPermissionToCreateLocation);
        return { hasError: true, message: 'Failed' };
      }
    } catch (e) {
      toastError(e, toastMessage.AUTH.loginFailed);
      logger.log(e, 'error in supervisor access');
    }
  }, []);

  return (
    <Modal
      title="Supervisor Override"
      closeClick={closeClick}
      maxWidth="282px"
      className="login_modal"
      dataTestId={`${dataTestId}-mdl`}
    >
      <div
        className="login"
        style={{ maxWidth: 'unset', alignItems: 'unset' }}
        data-test-id={`${dataTestId}-wrap`}
      >
        <div className="login_container" data-test-id={`${dataTestId}-cnt`}>
          <ControlledInput
            dataTestId={`${dataTestId}-sign-in-email-input`}
            control={control}
            name="email"
            title="Sign in"
            placeholder="username@curavel.com"
            rules={{
              required: validations.required('Email'),
            }}
          />
          <ControlledInput
            dataTestId={`${dataTestId}-password-input`}
            control={control}
            name="password"
            title="Password"
            placeholder="Password123!"
            type="password"
            rules={{
              required: validations.required('Password'),
            }}
          />
          <div style={{ marginBottom: 7 }}>
            <SubmitButton
              dataTestId={`${dataTestId}-approve-button`}
              title="Approve"
              onClick={handleSubmit(handleApproveSupervisor)}
              dirtyFields={dirtyFields}
              loading={isSubmitting}
              disableWhenUnchangedFields
              style={{ height: '46px', width: '142px', marginLeft: 'auto' }}
              disabled={isSubmitting}
              forceDisable={!isValid}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AuthModal;
